import React, { useState, useEffect, useRef } from "react";
import { Container } from "components/shared";
import styled from "styled-components";
import { getFieldValue, Placeholder } from "@sitecore-jss/sitecore-jss-react";
import cx from "classnames";
import useBreakpoint from "utils/hooks/useBreakpoint";
import HeartIcon from "components/icons/Heart";
import HeartFilled from "components/icons/HeartFilled";
import { useSpring, animated } from "react-spring";
import ShareIcon from "components/icons/Share";
import useScrollDirection from "utils/hooks/useScrollDirection";
import pageTypes from "utils/pageTypes";
import breakpoints from "utils/breakpoints";
import { useUiValue, setHasCommingSonnCommunity } from "stores/uiContext";
import { shareThis, getUrl } from "utils/shareThis";
import useFavorites from "utils/hooks/useFavorites";
import { useSitecoreContext } from "stores/sitecoreContext";
import { scrollToSection } from "utils/scrollTo";
import quickStartLogo from "assets/quickStartLogo.svg";
import { useCountryValue } from "stores/country";
import { isValidTitle } from "utils/validateTitle";
import { getDefaultFontSize } from "utils/getDefaultFontSize";
import Calculator from "components/icons/Calculator";
import Info from "components/icons/Info";
import useOnClickOutside from "utils/hooks/useOnClickOutside";
import useNewColorSchemaOptions from "utils/hooks/useNewColorSchemaOptions";
import { getPriceDifferential } from "utils/getPriceDifferential";

import loadable from "@loadable/component";
const Button = loadable(() => import("components/components/atoms/Button"));
const MortgageCalculatorModal = loadable(() =>
  import("components/components/organisms/MortgageCalculatorModal")
);

const StyledContainer = styled(Container)`
  ${({ minHeight, isMobile }) =>
    isMobile &&
    minHeight &&
    `  min-height:${minHeight}px;
  `}
`;

const Heading = styled.h1`
  @media ${breakpoints.md} {
    font-size: 2.875rem;
  }
`;
const PriceText = styled.p`
  @media ${breakpoints.md} {
    font-size: 2.875rem;
  }
`;

const Text = styled.p`
  letter-spacing: 0.14px;
`;

const StyledButton = styled(Button)`
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 3rem;
  padding-right: 3rem;
  height: 3.125rem;
`;

const Img = styled.img`
  width: 6.25rem;
  height: 3.75rem;
  border-radius: 0.625rem;
`;

const TooltipLogo = styled.div`
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  opacity: 0;
  width: 0;
  height: 0;
`;
const QuickStartLogoContainer = styled.div`
  &:hover ${TooltipLogo} {
    opacity: 1;
    height: auto;
    width: 50%;
    top: 4rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
`;

const Tooltip = styled.div`
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);

  @media ${breakpoints.md} {
    width: 19rem;
    left: 75%;
  }
`;

const getCTA = (rendering) => {
  try {
    const fields = rendering.placeholders["btn-cta"][0].fields;
    return getFieldValue(fields, "buttonCta");
  } catch (e) {
    console.log(e);
    return {};
  }
};

const getUTourCTA = (rendering) => {
  try {
    const fields = rendering.placeholders["home-specs"][0].fields;
    return getFieldValue(fields, "uTourFirstCtaLink");
  } catch (e) {
    console.log(e);
    return {};
  }
};

const TitleDetailsBlock = ({ fields, rendering }) => {
  const title = getFieldValue(fields, "title");
  const startingPriceLabel = getFieldValue(fields, "startingPriceLabel");
  const startingPrice = getFieldValue(fields, "startingPrice");
  const monthlyPrice = getFieldValue(fields, "monthlyPrice");
  const displayPreviousPrice = getFieldValue(fields, "displayPreviousPrice");
  const previousPriceQMI = getFieldValue(fields, "previousPriceQMI");
  const image = getFieldValue(fields, "image") || {};
  const currentPageType = getFieldValue(fields, "currentPageType");
  const lotJDEID = getFieldValue(fields, "lotJDEID");
  const rvGarage = getFieldValue(fields, "rvGarage");
  const preSellingHome = getFieldValue(fields, "preSellingHome");
  const componentCountry = getFieldValue(fields, "country");
  const communityStatus = getFieldValue(fields, "communityStatus");
  const [{ country }] = useCountryValue();
  const [
    { notificationHeight, nationalNotificationHeight },
    dispatch,
  ] = useUiValue();
  const { showNewSchemaColor } = useNewColorSchemaOptions();
  const mortgageAmountsModal = getFieldValue(fields, "mortgageAmountsModal");
  const tooltipText =
    country === "CAN"
      ? mortgageAmountsModal &&
        mortgageAmountsModal.mortgageAmountsCanada.tooltip
      : mortgageAmountsModal && mortgageAmountsModal.mortgageAmountsUSA.tooltip;

  const quickStartHomesTooltip = getFieldValue(
    fields,
    "quickStartHomesTooltip"
  );
  const priceDifferentialText =
    country === "USA"
      ? getFieldValue(fields, "priceDifferentialTextUSA")
      : getFieldValue(fields, "priceDifferentialTextCAN");

  const cta = getCTA(rendering) || {};
  const UTourCTA = getUTourCTA(rendering) || {};
  const [
    showModalMortgageCalculator,
    setShowModalMortgageCalculator,
  ] = useState(false);
  const [
    showModalMortgageCalculatorTooltip,
    setShowModalMortgageCalculatorTooltip,
  ] = useState(false);
  const tooltipRef = useRef();
  useOnClickOutside(tooltipRef, () => {
    setTimeout(() => {
      setShowModalMortgageCalculator(false);
    }, 100);
  });

  useEffect(() => {
    setTimeout(() => {
      if (communityStatus && communityStatus.toUpperCase() === "COMING SOON") {
        dispatch(setHasCommingSonnCommunity(true));
      }
    });
  }, [communityStatus]);

  const { favoritesList, saveFavorite, deleteFavorite } = useFavorites();
  const { ContextID = {} } = useSitecoreContext() || {};
  const id = ContextID.value;
  const isFavorite = favoritesList.includes(id);

  const [{ notificationActive }] = useUiValue();

  const scrollDirection = useScrollDirection();

  const [fixedBarVisible, setFixedBarVisible] = useState(false);
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs", "md"].includes(breakpoint);
  const [defaultFontSize, setDefaultFontSize] = useState();
  useEffect(() => {
    const font = getDefaultFontSize();
    setDefaultFontSize(font);
  }, []);

  const [notificationTopHeight, setNotificationTopHeight] = useState(0);
  useEffect(() => {
    setNotificationTopHeight(notificationHeight + nationalNotificationHeight);
  }, [notificationHeight, nationalNotificationHeight]);

  const fixedBarStyle = useSpring({
    config: { duration: 100 },
    top: fixedBarVisible
      ? scrollDirection === "up"
        ? defaultFontSize <= 18
          ? notificationTopHeight + 80
          : notificationTopHeight + 150
        : defaultFontSize <= 18
        ? notificationTopHeight
        : notificationTopHeight + 70
      : -100,
  });

  const handleOnScroll = () => {
    if (!isMobile) {
      const currentScrollY = window.scrollY;
      const offset = notificationActive ? 360 : 300;
      if (currentScrollY > offset) {
        setFixedBarVisible(true);
      } else if (fixedBarVisible) {
        setFixedBarVisible(false);
      }
    }
  };

  const handleFavoriteClick = (id) => {
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleOnScroll);
    return () => window.removeEventListener("scroll", handleOnScroll);
  }, [handleOnScroll]);

  const handleCTAClick = () => {
    scrollToSection("contact");
    window.dataLayer.push({ event: "register" });
  };

  const [hasSitemap, setHasSitemap] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      if (typeof window !== "undefined" && window.document) {
        const exitsSitemap = window.document.getElementById("sitemap");
        if (exitsSitemap) setHasSitemap(true);
      }
    }, 0);
  }, []);

  const getCurrentLocation = () => {
    if (typeof window !== "undefined" && window.document) {
      return window.location.pathname;
    }
    return "";
  };

  return (
    <>
      <animated.div
        id="detailsBlockBar"
        className={cx(
          "fixed w-screen top-0 flex bg-mattamy-blue-light items-center justify-between z-30 h-20",
          { hidden: !fixedBarVisible || isMobile }
        )}
        style={fixedBarStyle}
      >
        <div className="ml-4 flex justify-center items-center">
          {image && image.src && (
            <div className="m-1">
              <Img
                src={image.src}
                alt={image.alt}
                className="object-cover w-full h-full"
              ></Img>
            </div>
          )}
          <div className="mx-12 text-left">
            {hasSitemap ? (
              <a href={getCurrentLocation() + "#sitemap"}>
                {isValidTitle(title) && (
                  <h3 className="underline mt-2 text-white font-trade-gothic-20 leading-9 tracking-tighter text-2xl leading-none">
                    {title}
                  </h3>
                )}
              </a>
            ) : (
              isValidTitle(title) && (
                <h3 className="mt-2 text-white font-trade-gothic-20 leading-9 tracking-tighter text-2xl leading-none">
                  {title}
                </h3>
              )
            )}
            <Placeholder
              name="home-specs"
              rendering={rendering}
              navBar
              lotJDEID={lotJDEID}
              rvGarage={rvGarage}
            />
          </div>
        </div>
        <div className="flex justify-center">
          <div className="mr-8 text-right">
            {startingPrice &&
              startingPrice !== "$0" &&
              startingPrice !== "$" && (
                <h2 className="mt-2 text-2xl text-white font-trade-gothic-20 tracking-tighter leading-none">
                  {startingPrice}
                </h2>
              )}

            {((country === "USA" &&
              mortgageAmountsModal &&
              mortgageAmountsModal.displayMortgageModalUSA) ||
              (country === "CAN" &&
                mortgageAmountsModal &&
                mortgageAmountsModal.displayMortgageModalCanada)) &&
              monthlyPrice &&
              monthlyPrice !== "$0" &&
              monthlyPrice !== "$" && (
                <Text className="text-white font-graphie text-base">
                  {country === "CAN"
                    ? mortgageAmountsModal &&
                      mortgageAmountsModal.mortgageAmountsCanada
                        .cardDisplayText + " "
                    : mortgageAmountsModal &&
                      mortgageAmountsModal.mortgageAmountsUSA.cardDisplayText +
                        " "}
                  {country === "CAN"
                    ? mortgageAmountsModal &&
                      mortgageAmountsModal.mortgageAmountsCanada
                        .cardDisplayPrice &&
                      monthlyPrice
                    : mortgageAmountsModal &&
                      mortgageAmountsModal.mortgageAmountsUSA
                        .cardDisplayPrice &&
                      monthlyPrice}
                </Text>
              )}
          </div>
          <div className="mx-2 flex items-center justify-center">
            {UTourCTA && UTourCTA.text && (
              <StyledButton
                className="mr-2"
                color="white"
                textColor="action-blue"
                smallCTA
                role="link"
                href={UTourCTA.href}
                target={UTourCTA.target}
              >
                {UTourCTA.text}
              </StyledButton>
            )}

            {cta && cta.text && (
              <StyledButton
                color="white"
                textColor="action-blue"
                smallCTA
                onClick={handleCTAClick}
                role="link"
              >
                {cta.text}
              </StyledButton>
            )}
          </div>
          <div className="mr-8 flex justify-center items-center">
            <div
              className="flex items-center justify-center cursor-pointer"
              onClick={() => handleFavoriteClick(id)}
              aria-label={
                isFavorite ? "Mark as unfavorite" : "Mark as favorite"
              }
              aria-pressed={isFavorite ? "true" : "false"}
              role="button"
            >
              {isFavorite ? (
                <HeartFilled className="mx-2 h-5 w-5 text-white" />
              ) : (
                <HeartIcon className="mx-2 text-white" />
              )}
            </div>

            <div
              className="st-custom-button"
              onClick={() => {
                shareThis({
                  dataNetwork: "email",
                  dataUrl: getUrl(),
                });
              }}
            >
              <ShareIcon className="mx-2 text-white" />
            </div>
          </div>
        </div>
      </animated.div>
      <StyledContainer
        className="justify-between pb-6"
        //isMobile={isMobile}
        //minHeight={250}
      >
        <div
          className={cx(
            "flex flex-col md:flex-row md:justify-between pb-0 pt-5 md:pb-6",
            {
              "md:items-baseline": !startingPriceLabel,
              "md:items-center": startingPriceLabel,
            }
          )}
        >
          <div className="mb-6 md:mb-0">
            {startingPriceLabel && (
              <div className="-mt-2 text-mattamy-blue h-3 md:h-8"></div>
            )}
            <div className="flex flex-row w-full">
              {hasSitemap ? (
                <a href={getCurrentLocation() + "#sitemap"}>
                  <Heading
                    className={cx(
                      "underline flex text-3xl font-bold font-trade-gothic-20 leading-tiny mt-1",
                      {
                        "text-mattamy-blue ": !showNewSchemaColor,
                        "text-black ": showNewSchemaColor,
                      }
                    )}
                  >
                    <span>{title}</span>
                  </Heading>
                </a>
              ) : (
                <Heading
                  className={cx(
                    "flex text-3xl font-bold font-trade-gothic-20 leading-tiny mt-1",
                    {
                      "text-mattamy-blue ": !showNewSchemaColor,
                      "text-black ": showNewSchemaColor,
                    }
                  )}
                >
                  <span>{title}</span>
                </Heading>
              )}
              {currentPageType === pageTypes.QmiPage &&
                preSellingHome &&
                componentCountry === "United States" && (
                  <>
                    <QuickStartLogoContainer className="rounded border-mattamy-blue-light border-2 h-6 mt-1 md:h-8 md:mt-2 ml-4">
                      <img
                        className="object-cover h-full rounded"
                        src={quickStartLogo}
                        alt="quickStartLogo..."
                      />
                      {quickStartHomesTooltip && (
                        <TooltipLogo className="-ml-16 md:ml-0 absolute bg-white rounded z-10 text-left text-action-blue text-sm cursor-default">
                          {quickStartHomesTooltip}
                        </TooltipLogo>
                      )}
                    </QuickStartLogoContainer>
                  </>
                )}
            </div>
            <Placeholder
              name="home-specs"
              rendering={rendering}
              lotJDEID={lotJDEID}
              rvGarage={rvGarage}
            />
          </div>

          <div
            className={cx("md:text-right ", {
              "text-mattamy-blue ": !showNewSchemaColor,
              "text-black ": showNewSchemaColor,
            })}
          >
            {startingPrice && startingPrice !== "$0" && startingPrice !== "$" && (
              <>
                {startingPriceLabel && (
                  <p className="text-sm md:text-xl tracking-wide">
                    {startingPriceLabel}
                  </p>
                )}

                <PriceText className="font-bold font-trade-gothic-20 text-3xl leading-tiny mt-1">
                  <span>{startingPrice} </span>
                  {displayPreviousPrice && (
                    <span className="ml-4 line-through text-mattamy-gray-light">
                      {previousPriceQMI}{" "}
                    </span>
                  )}
                  {displayPreviousPrice && (
                    <>
                      <span className="ml-4 font-graphie text-xs leading-2 -mt-2 text-mattamy-gray-light ">
                        {priceDifferentialText}{" "}
                      </span>
                      <span className="text-lg md:text-2xl leading-5 font-trade-gothic-20 text-mattamy-gray-light ">
                        {getPriceDifferential(previousPriceQMI, startingPrice)}
                      </span>
                    </>
                  )}
                </PriceText>
              </>
            )}

            {((country === "USA" &&
              mortgageAmountsModal &&
              mortgageAmountsModal.displayMortgageModalUSA) ||
              (country === "CAN" &&
                mortgageAmountsModal &&
                mortgageAmountsModal.displayMortgageModalCanada)) &&
              monthlyPrice &&
              monthlyPrice !== "$0" &&
              monthlyPrice !== "$" && (
                <div className="relative flex md:justify-end">
                  <Calculator className={"h-6"} />
                  <button
                    className={cx(
                      "text-md font-trade-gothic-20 uppercase font-bold leading-8 md:leading-7 tracking-wide cursor-pointer hover:underline"
                    )}
                    tabIndex={0}
                    onClick={() => {
                      if (typeof window !== "undefined") {
                        window.dataLayer.push({
                          event: "MortgageCalcLinkClickS",
                        });
                        window.gtag("event", "MortgageCalcLinkClick");
                      }
                      setShowModalMortgageCalculator(true);
                    }}
                  >
                    {country === "CAN"
                      ? mortgageAmountsModal &&
                        mortgageAmountsModal.mortgageAmountsCanada
                          .cardDisplayText + " "
                      : mortgageAmountsModal &&
                        mortgageAmountsModal.mortgageAmountsUSA
                          .cardDisplayText + " "}
                    {country === "CAN"
                      ? mortgageAmountsModal &&
                        mortgageAmountsModal.mortgageAmountsCanada
                          .cardDisplayPrice &&
                        monthlyPrice
                      : mortgageAmountsModal &&
                        mortgageAmountsModal.mortgageAmountsUSA
                          .cardDisplayPrice &&
                        monthlyPrice}
                  </button>
                  <svg
                    className={cx("w-6 mb-1 mr-2 transform rotate-180")}
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      fill="currentColor"
                      d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"
                    />
                  </svg>
                  {tooltipText && tooltipText.length > 0 && (
                    <button
                      className="mb-1"
                      onClick={(e) => {
                        setShowModalMortgageCalculatorTooltip(true);
                      }}
                    >
                      <Info className={" h-4 transform"} />
                    </button>
                  )}
                </div>
              )}

            {showModalMortgageCalculatorTooltip && tooltipText && (
              <Tooltip
                className="absolute px-6 py-4 bg-white rounded z-10 text-left text-action-blue text-sm cursor-default"
                data-aos="fade-down"
                ref={tooltipRef}
              >
                {tooltipText}
              </Tooltip>
            )}
          </div>
        </div>
      </StyledContainer>

      <MortgageCalculatorModal
        isOpen={showModalMortgageCalculator}
        onClose={() => {
          setShowModalMortgageCalculator(false);
        }}
        disclaimer={
          country === "USA"
            ? mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsUSA &&
              mortgageAmountsModal.mortgageAmountsUSA.disclaimerText
            : mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsCanada &&
              mortgageAmountsModal.mortgageAmountsCanada.disclaimerText
        }
        ctaText={
          country === "USA"
            ? mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsUSA &&
              mortgageAmountsModal.mortgageAmountsUSA.ctaText
            : mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsCanada &&
              mortgageAmountsModal.mortgageAmountsCanada.ctaText
        }
        ctaTitle={
          country === "USA"
            ? mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsUSA &&
              mortgageAmountsModal.mortgageAmountsUSA.ctaTitle
            : mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsCanada &&
              mortgageAmountsModal.mortgageAmountsCanada.ctaTitle
        }
        ctaURL={
          country === "USA"
            ? mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsUSA &&
              mortgageAmountsModal.mortgageAmountsUSA.ctaurl &&
              mortgageAmountsModal.mortgageAmountsUSA.ctaurl.href
            : mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsCanada &&
              mortgageAmountsModal.mortgageAmountsCanada.ctaurl &&
              mortgageAmountsModal.mortgageAmountsCanada.ctaurl.href
        }
        amountValue={
          startingPrice && startingPrice.replace("$", "").replace(/,/g, "")
        }
        downpaymentInit={
          country === "USA"
            ? mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsUSA &&
              mortgageAmountsModal.mortgageAmountsUSA.paymentPercentage
            : mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsCanada &&
              mortgageAmountsModal.mortgageAmountsCanada.paymentPercentage
        }
        interestRateInit={
          country === "USA"
            ? mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsUSA &&
              mortgageAmountsModal.mortgageAmountsUSA.interestRate
            : mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsCanada &&
              mortgageAmountsModal.mortgageAmountsCanada.interestRate
        }
        loanTermInit={
          country === "USA"
            ? mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsUSA &&
              mortgageAmountsModal.mortgageAmountsUSA.termInYears
            : mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsCanada &&
              mortgageAmountsModal.mortgageAmountsCanada.termInYears
        }
        title={
          country === "USA"
            ? mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsUSA &&
              mortgageAmountsModal.mortgageAmountsUSA.mortgageTitle
            : mortgageAmountsModal &&
              mortgageAmountsModal.mortgageAmountsCanada &&
              mortgageAmountsModal.mortgageAmountsCanada.mortgageTitle
        }
      />
    </>
  );
};

export default TitleDetailsBlock;
